import { useEffect, useMemo, useState } from 'react'

import { useInView } from '@/lib/useInView'
import { useMediaQuery } from '@/lib/useMediaQuery'
import { useReducedMotion } from '@/lib/useReducedMotion'

import { EdgeLayer } from './EdgeLayer'
import { GroupCard } from './GroupCard'
import { NodeCard } from './NodeCard'
import { TracerLayer } from './TracerLayer'
import { anchorFor, nodePos, type GraphScene } from './types'

interface Props {
  scene: GraphScene
  /** Only the active scene runs its schedule. */
  active: boolean
  /** Bumped on every tab click so re-clicking the active tab replays it. */
  resetKey: number
}

const HOLD_MS = 2200

export function GraphCanvas({ scene, active, resetKey }: Props) {
  const reduced = useReducedMotion()
  const isMobile = useMediaQuery('(max-width: 640px)')
  const { ref: rootRef, inView } = useInView<HTMLDivElement>(0.05)
  const [stepIdx, setStepIdx] = useState(-1)

  const { schedule, startOffsetMs } = scene

  useEffect(() => {
    if (!active) {
      setStepIdx(-1)
      return
    }

    // Reduced motion: land on the terminal frame — every node lit, every edge
    // drawn — and stop. No timers, no loop. (The reference site accepts a
    // reduced flag here but never branches on it, so its hero loops forever
    // regardless; this is the fix.)
    if (reduced) {
      setStepIdx(schedule.length - 1)
      return
    }

    // Off-screen: don't run a timer chain for a hero nobody is looking at.
    if (!inView) return

    let cancelled = false
    const timers: number[] = []

    const runOnce = () => {
      if (cancelled) return
      setStepIdx(-1)

      let elapsed = 120
      schedule.forEach((step, idx) => {
        timers.push(
          window.setTimeout(() => {
            if (!cancelled) setStepIdx(idx)
          }, elapsed),
        )
        elapsed += step.ms
      })

      timers.push(
        window.setTimeout(() => {
          if (!cancelled) runOnce()
        }, elapsed + HOLD_MS),
      )
    }

    timers.push(window.setTimeout(runOnce, startOffsetMs))

    return () => {
      cancelled = true
      timers.forEach((t) => window.clearTimeout(t))
    }
  }, [active, resetKey, reduced, inView, schedule, startOffsetMs])

  // Cumulative: a node, once lit, stays lit for the rest of the cycle.
  const visited = useMemo(() => {
    const lit = new Set<string>()
    for (let i = 0; i <= stepIdx && i < schedule.length; i++) {
      for (const id of schedule[i].show) lit.add(id)
    }
    return lit
  }, [schedule, stepIdx])

  const focused = stepIdx >= 0 && !reduced ? schedule[stepIdx]?.focus : null

  const groups = scene.groups
  const groupedIds = useMemo(
    () => new Set((groups ?? []).flatMap((g) => g.ids)),
    [groups],
  )

  return (
    <div
      ref={rootRef}
      className="tl-canvas-grid relative w-full"
      style={{ height: 'min(66vh, 640px)', minHeight: 320 }}
      role="img"
      aria-label={`Thinai platform diagram: ${scene.nodes.map((n) => n.name).join(', ')}`}
    >
      <EdgeLayer
        nodes={scene.nodes}
        edges={scene.edges}
        groups={groups}
        isMobile={isMobile}
        visited={visited}
      />

      {groups?.map((group) => {
        const pos = anchorFor(group.ids[0], scene.nodes, groups, isMobile)
        if (!pos) return null
        return (
          <GroupCard
            key={group.ids.join('-')}
            group={group}
            x={pos.x}
            y={pos.y}
            visited={group.ids.some((id) => visited.has(id))}
            focused={Boolean(focused && group.ids.includes(focused))}
          />
        )
      })}

      <TracerLayer edges={scene.edges} visited={visited} reduced={reduced} />

      {/* Card layer above the tracers, so dots pass behind cards. */}
      <div className="absolute inset-0" style={{ zIndex: 5 }}>
        {scene.nodes
          .filter((n) => !groupedIds.has(n.id))
          .map((node) => {
            const p = nodePos(node, isMobile)
            return (
              <NodeCard
                key={node.id}
                node={node}
                x={p.x}
                y={p.y}
                visited={visited.has(node.id)}
                focused={focused === node.id}
              />
            )
          })}
      </div>
    </div>
  )
}
